import { useTheme } from "@mui/system";
import { ArrowRight, Image } from "@pmag/icons";
import { Chip, Typography } from "@pmag/ui-components";
import { Routes } from "@src/context/router/RouterContext";
import { type GQLVisualSelectorModelFragment } from "@src/graphql.generated";
import { useGlobalState } from "@src/hooks/useGlobalState";
import { useRouter } from "@src/hooks/useRouter";
import { resolveLogoForBrand } from "@src/images/brands.utils";
import type * as React from "react";

import {
    BrandLogoContainer,
    BrandLogoImage,
    Card,
    ChipContainer,
    ImageContainer,
    ModelImage,
    ModelVariantContainer,
    ModelVariantRightContainer,
    ModelVariantTouchable,
} from "./VehicleCard.styles";

export const dataTestId = {
    container: "sparePartsFinder.searchResult.searchResultCard.container",
    modelImage: "sparePartsFinder.searchResult.searchResultCard.modelImage",
    modelDescription: "sparePartsFinder.searchResult.searchResultCard.modelDescription",
    buttonSelectModelVariant: "sparePartsFinder.searchResult.searchResultCard.button.selectModelVariant",
};

export interface VisualSelectorGroupedModel extends GQLVisualSelectorModelFragment {
    additionalColorGroupModels?: GQLVisualSelectorModelFragment[];
}

interface VehicleCardProps {
    model: VisualSelectorGroupedModel;
    showBrandLogo?: boolean;
}

export const VehicleCard: React.FunctionComponent<VehicleCardProps> = ({ model, showBrandLogo, ...restProps }) => {
    const theme = useTheme();
    const { setRoute } = useRouter();
    const { setModelId } = useGlobalState();

    const additionalModelsCount = model.additionalColorGroupModels ? model.additionalColorGroupModels.length : 0;

    const onModelVariantSelected = (modelId: string) => {
        setRoute(Routes.ComponentGroupOverview);
        setModelId(modelId);
    };

    return (
        <Card data-testid={dataTestId.container} {...restProps}>
            <ImageContainer>
                {model.assetPath && <ModelImage src={model.assetPath} data-testid={dataTestId.modelImage} />}

                {model.color && (
                    <ChipContainer>
                        <Image size="small" />

                        <Typography variant="label-01">{model.color}</Typography>
                    </ChipContainer>
                )}

                {showBrandLogo && model.brand && (
                    <BrandLogoContainer>
                        <BrandLogoImage src={resolveLogoForBrand(model.brand.shortName)} />
                    </BrandLogoContainer>
                )}
            </ImageContainer>

            <ModelVariantContainer>
                <ModelVariantTouchable
                    showBorderBottom={additionalModelsCount > 1}
                    onClick={() => model.articleNumber && onModelVariantSelected(model.id)}
                    data-testid={dataTestId.buttonSelectModelVariant}
                >
                    <Typography variant="body-short-02" data-testid={dataTestId.modelDescription}>
                        {model.description}
                    </Typography>

                    <ModelVariantRightContainer>
                        {model.modelSpecifications.map((spec) => (
                            <Chip color="grey" key={spec.id}>
                                {spec.name}
                            </Chip>
                        ))}

                        <ArrowRight color={theme.palette.core.grey._60} />
                    </ModelVariantRightContainer>
                </ModelVariantTouchable>

                {model.additionalColorGroupModels?.map(
                    (additionalModel, index) =>
                        additionalModel.articleNumber && (
                            <ModelVariantTouchable
                                key={model.id}
                                showBorderBottom={index < additionalModelsCount - 1}
                                onClick={() => additionalModel.articleNumber && onModelVariantSelected(additionalModel.id)}
                            >
                                <Typography variant="body-short-02">{additionalModel.description}</Typography>

                                <ModelVariantRightContainer>
                                    {additionalModel.modelSpecifications.map((spec) => (
                                        <Chip color="grey" key={spec.id}>
                                            {spec.name}
                                        </Chip>
                                    ))}

                                    <ArrowRight color={theme.palette.core.grey._60} />
                                </ModelVariantRightContainer>
                            </ModelVariantTouchable>
                        ),
                )}
            </ModelVariantContainer>
        </Card>
    );
};
